import React, {
  createContext,
  useContext,
  useCallback,
  useRef,
  type ReactNode,
} from "react"

type BalanceContextType = {
  refetchBalances: () => void
  setRefetchFunction: (refetchFn: () => void) => void
  invalidateOvertimeCache: () => void
}

const BalanceContext = createContext<BalanceContextType | undefined>(undefined)

export const useBalanceContext = () => {
  const context = useContext(BalanceContext)
  if (!context) {
    throw new Error("useBalanceContext must be used within a BalanceProvider")
  }
  return context
}

export const BalanceProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  let refetchFunction: (() => void) | null = null
  let overtimeRefetchFunction: (() => void) | null = null

  // Debounce refetch calls to prevent rapid successive executions
  const refetchTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const lastRefetchTime = useRef<number>(0)
  const REFETCH_DEBOUNCE_MS = 100 // 100ms debounce

  const setRefetchFunction = useCallback((refetchFn: () => void) => {
    refetchFunction = refetchFn
  }, [])

  const setOvertimeRefetchFunction = useCallback((refetchFn: () => void) => {
    overtimeRefetchFunction = refetchFn
  }, [])

  const refetchBalances = useCallback(() => {
    const now = Date.now()

    // Prevent rapid successive refetches
    if (now - lastRefetchTime.current < REFETCH_DEBOUNCE_MS) {
      // Clear existing timeout and set new one
      if (refetchTimeoutRef.current) {
        clearTimeout(refetchTimeoutRef.current)
      }

      refetchTimeoutRef.current = setTimeout(() => {
        if (refetchFunction) {
          refetchFunction()
          lastRefetchTime.current = Date.now()
        }
      }, REFETCH_DEBOUNCE_MS)

      return
    }

    // Execute refetch immediately if enough time has passed
    if (refetchFunction) {
      refetchFunction()
      lastRefetchTime.current = now
    }
  }, [])

  const invalidateOvertimeCache = useCallback(() => {
    if (overtimeRefetchFunction) {
      overtimeRefetchFunction()
    }
  }, [])

  const value: BalanceContextType = {
    refetchBalances,
    setRefetchFunction,
    invalidateOvertimeCache,
  }

  return (
    <BalanceContext.Provider value={value}>{children}</BalanceContext.Provider>
  )
}
